import { useQuery } from "@tanstack/react-query";
import { api } from "../api";
import { Icon } from "../lib/Icon";
import { ErrorNote, Loading, Page } from "./common";

const CARD: React.CSSProperties = { background: "#fff", borderRadius: 3, boxShadow: "inset 0 0 0 1px rgba(0,0,0,.07)", padding: "14px 18px" };
const KIND_TONE: Record<string, { bg: string; fg: string }> = {
  Fixed: { bg: "#e8f4ee", fg: "#1f8a5b" },
  Improved: { bg: "#e8f0f3", fg: "#2a6f8a" },
  New: { bg: "#faece3", fg: "#c0612f" },
};

// Read-only preview of what a client admin sees: the announcement banner, What's
// new (published changelog), and the public Now/Next/Later roadmap. Reuses the
// staff /updates data — no separate store.
export function AdminPreview() {
  const { data, isLoading, error } = useQuery({ queryKey: ["updates"], queryFn: api.updates });
  if (isLoading) return <Loading />;
  if (error) return <ErrorNote error={error} />;
  // A client admin only sees PUBLISHED, non-future releases — not staged drafts.
  const now = Date.now();
  const releases = (data?.releases ?? []).filter((r) => r.publishedAt && Date.parse(r.publishedAt) <= now);
  const roadmap = data?.roadmap ?? { now: [], next: [], later: [] };
  // The banner is the most recent already-sent announcement that targets the banner channel.
  const banner = (data?.announcements ?? []).find((a) => !a.scheduled && a.channels.includes("banner")) ?? null;

  return (
    <Page>
      <div>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600, letterSpacing: "-0.6px" }}>Client admin preview</h1>
        <div style={{ fontSize: 14, color: "#8a8e88", marginTop: 3 }}>What a client admin sees in their console — banner, What's new, and the public roadmap.</div>
      </div>

      {banner && (
        <div style={{ display: "flex", gap: 10, alignItems: "center", background: "#eef4f1", border: "1px solid #d6e7de", borderRadius: 8, padding: "11px 14px" }}>
          <Icon name="megaphone" size={16} color="#006747" />
          <div>
            <div style={{ fontSize: 13, fontWeight: 600, color: "#00543a" }}>{banner.title}</div>
            {banner.body && <div style={{ fontSize: 12, color: "#3a463f", marginTop: 1 }}>{banner.body}</div>}
          </div>
        </div>
      )}

      <div style={CARD}>
        <div style={{ fontSize: 15, fontWeight: 600, marginBottom: 8 }}>What's new</div>
        {releases.length === 0 ? (
          <div style={{ color: "#8a8e88", fontSize: 13 }}>No published updates yet.</div>
        ) : (
          releases.map((rel) => (
            <div key={rel.id} style={{ padding: "10px 0", borderTop: "1px solid #f4f3ee" }}>
              <div style={{ display: "flex", alignItems: "baseline", gap: 8 }}>
                <span style={{ fontFamily: "'Space Mono',monospace", fontSize: 12.5, fontWeight: 700 }}>{rel.version}</span>
                {rel.publishedAt && <span style={{ fontSize: 11, color: "#8a8e88" }}>{rel.publishedAt.slice(0, 10)}</span>}
              </div>
              {rel.items.map((it, i) => (
                <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                  <span style={{ fontSize: 10, fontWeight: 600, padding: "1px 7px", borderRadius: 999, background: (KIND_TONE[it.kind] ?? { bg: "#f0efe9" }).bg, color: (KIND_TONE[it.kind] ?? { fg: "#6b706a" }).fg }}>{it.kind}</span>
                  <div style={{ fontSize: 12.5 }}>
                    <span style={{ fontWeight: 500 }}>{it.title}</span>
                    {it.body && <div style={{ color: "#8a8e88", fontSize: 12 }}>{it.body}</div>}
                  </div>
                </div>
              ))}
            </div>
          ))
        )}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(240px,1fr))", gap: 16 }}>
        {(["now", "next", "later"] as const).map((bucket) => (
          <div key={bucket} style={CARD}>
            <div style={{ fontSize: 13, fontWeight: 600, textTransform: "capitalize", marginBottom: 8 }}>{bucket}</div>
            {roadmap[bucket].length === 0 ? (
              <div style={{ color: "#8a8e88", fontSize: 12.5 }}>Nothing here yet.</div>
            ) : (
              roadmap[bucket].map((r) => (
                <div key={r.id} style={{ padding: "7px 0", borderTop: "1px solid #f4f3ee", fontSize: 12.5 }}>
                  <div style={{ fontWeight: 500 }}>{r.title}</div>
                  {r.body && <div style={{ color: "#8a8e88", fontSize: 12 }}>{r.body}</div>}
                </div>
              ))
            )}
          </div>
        ))}
      </div>
    </Page>
  );
}
